import { Component, computed, input, InputSignal, Signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { DisplayCurrencyModule } from '@lib/util';
import { InvoiceItemDeviationComponent } from './invoice-item-deviation.component';
import { InvoiceItemQuantityStatusComponent } from './invoice-item-quantity-status.component';
import { InvoiceItemOrderComparison } from '../types/InvoiceOrderComparison';

/** Price difference per single unit, prepared so the template never has to juggle a nullable number. */
export interface IUnitPriceDifference {
    amount: number;
    percent: number | null;
    currency: string;
    isPositive: boolean;
    digitsInfo: string;
}

/**
 * Two decimals are the precision of a price, but a price quoted per 100 or per 1000 is a fraction of
 * a cent per single unit and would read as zero. Such an amount is shown with as many decimals as it
 * takes to stay a number.
 */
function digitsInfoFor(amount: number): string {
    return Math.abs(amount) < 0.005 ? '1.2-5' : '1.2-2';
}

/** Both tax rates of a deviating tax row, stated side by side instead of as a difference. */
export interface ITaxRates {
    invoice: number;
    order: number;
}

/**
 * Full-width block under an invoice position that states how it compares to its order position
 * (UC_2026).
 *
 * <p>Everything the comparison has to say lives in this one block and in one grid — field, invoice
 * value, order value, difference — because spreading the statement over the slots of the position
 * hides the deviation between unrelated values and makes a neutral hint look like a problem.
 *
 * <p>Three colour steps carry the severity and nothing else: red is a deviation, blue and green are
 * neutral hints, grey means not compared.
 */
@Component({
    selector: 'invoice-item-order-comparison',
    imports: [
        CommonModule,
        TranslateModule,
        DisplayCurrencyModule,
        InvoiceItemDeviationComponent,
        InvoiceItemQuantityStatusComponent,
    ],
    templateUrl: './invoice-item-order-comparison.component.html',
    styleUrl: './invoice-item-order-comparison.component.scss',
})
export class InvoiceItemOrderComparisonComponent {
    public readonly comparison: InputSignal<InvoiceItemOrderComparison> =
        input.required<InvoiceItemOrderComparison>();

    /** Already translated unit name of the invoice position. */
    public readonly invoiceUnit: InputSignal<string> = input<string>('');

    /** Already translated unit name of the order position. */
    public readonly orderUnit: InputSignal<string> = input<string>('');

    /** Discount of the invoice position in percent; the compared price is the one before it. */
    public readonly discountPercentage: InputSignal<number> = input<number>(0);

    public readonly hasGoodsReceipt: InputSignal<boolean> = input<boolean>(true);

    /** Whether the document is a credit note, which takes its quantity back. */
    public readonly creditNote: InputSignal<boolean> = input<boolean>(false);

    /**
     * Unit of the ordered and the still open quantity. While the units differ these quantities are the
     * order's, so they are named in the order unit; otherwise the invoice unit is used, which also
     * stands in for an order position without a unit of its own.
     */
    protected readonly orderedQuantityUnit: Signal<string> = computed(() =>
        this.comparison().unitDeviates ? this.orderUnit() : this.invoiceUnit(),
    );

    /** No goods receipt is to be expected for a credit note, so its absence is no hint there. */
    protected readonly showsGoodsReceiptHint: Signal<boolean> = computed(
        () => !this.hasGoodsReceipt() && !this.creditNote(),
    );

    protected readonly deviationCount: Signal<number> = computed(() => this.comparison().deviationCount);

    protected readonly showsBillingRow: Signal<boolean> = computed(() => this.comparison().quantity !== null);

    /**
     * Prices in different currencies are no deviation of their own, but both are still shown next to
     * the currency row — unmarked and without a difference.
     */
    protected readonly showsPriceRow: Signal<boolean> = computed(
        () => this.comparison().priceDeviates || this.comparison().currencyDeviates,
    );

    /**
     * "Price before discount as ordered" may only be said while the price row reports nothing and the
     * order actually states a price.
     */
    protected readonly discountedPriceIsAsOrdered: Signal<boolean> = computed(
        () => !this.showsPriceRow() && this.comparison().price?.orderValue != null,
    );

    protected readonly showsTable: Signal<boolean> = computed(
        () => this.comparison().linkedToOrder && (this.deviationCount() > 0 || this.showsBillingRow()),
    );

    protected readonly unitPriceDifference: Signal<IUnitPriceDifference | null> = computed(() => {
        const item = this.comparison();
        const amount = item.unitPriceDifference;
        const currency = item.price?.invoiceValue?.currency ?? null;

        if (amount === null || currency === null || item.unitPriceIsEqual) {
            return null;
        }

        return {
            amount,
            percent: item.unitPriceDifferencePercent,
            currency,
            isPositive: amount > 0,
            digitsInfo: digitsInfoFor(amount),
        };
    });

    /** Decimals of the "corresponds to … per unit" line, which has the same precision problem. */
    protected readonly unitPriceDigitsInfo: Signal<string> = computed(() =>
        digitsInfoFor(this.comparison().invoiceUnitPrice ?? 0),
    );

    /**
     * Only when the price quantity really differs may the table say that it is the only difference.
     * A price flagged as different for another reason — a differing currency inside the amount —
     * would otherwise be explained away.
     */
    protected readonly onlyPriceQuantityDiffers: Signal<boolean> = computed(
        () => this.comparison().unitPriceIsEqual && (this.comparison().priceQuantity?.isDifferent ?? false),
    );

    protected readonly taxRates: Signal<ITaxRates | null> = computed(() => {
        const invoice = this.comparison().taxInPercent?.invoiceValue ?? null;
        const order = this.comparison().taxInPercent?.orderValue ?? null;

        return invoice === null || order === null ? null : { invoice, order };
    });
}
